import { alpha } from '@mui/material/styles';

function createGradient(color1: string, color2: string) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

interface GradientsPaletteOptions {
  primary: string;
  info: string;
  success: string;
  warning: string;
  error: string;
  other: string;
}

interface ChartPaletteColorsOptions {
  1: string;
  2: string;
  3: string;
  4: string;
}

interface ChartPaletteOptions {
  1: ChartPaletteColorsOptions;
  2: ChartPaletteColorsOptions;
  3: ChartPaletteColorsOptions;
  4: ChartPaletteColorsOptions;
  5: ChartPaletteColorsOptions;
  6: ChartPaletteColorsOptions;
}

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
  interface Palette {
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
  }
  interface PaletteOptions {
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
  }
}

declare module '@mui/material' {
  interface Color {
    0: string;
    500_8: string;
    500_12: string;
    500_16: string;
    500_24: string;
    500_32: string;
    500_48: string;
    500_56: string;
    500_80: string;
  }
}

// SETUP COLORS
const PRIMARY = {
  lighter: '#F4DEF9',
  light: '#C795DD',
  main: '#6F4290',
  dark: '#412167',
  darker: '#412167',
  // darker: '#005249',
};
const SECONDARY = {
  // lighter: '#D6E4FF',
  lighter: '#DDE9FF',
  light: '#DDE9FF',
  main: '#5680FF',
  dark: '#2B46B7',
  darker: '#2B46B7',
  // darker: '#091A7A',
};
const INFO = {
  lighter: '#C9ECFB',
  light: '#C9ECFB',
  // light: '#74CAFF',
  main: '#005DB8',
  main_16: alpha('#005DB8', 0.16),
  dark: '#003584',
  darker: '#003584',
  // darker: '#04297A',
};
const SUCCESS = {
  lighter: '#E1F9CF',
  light: '#E1F9CF',
  // light: '#AAF27F',
  main: '#258C17',
  main_16: alpha('#258C17', 0.16),
  dark: '#0B640F',
  darker: '#0B640F',
  // darker: '#08660D',
};
const WARNING = {
  lighter: '#FCF1CA',
  light: '#F1C561',
  // light: '#FFE16A',
  main: '#D18101',
  main_16: alpha('#D18101', 0.16),
  dark: '#965100',
  darker: '#642E00',
};
const ERROR = {
  lighter: '#FAD5D3',
  light: '#FAD5D3',
  // light: '#FFA48D',
  main: '#AA2553',
  main_16: alpha('#AA2553', 0.16),
  dark: '#7A124B',
  darker: '#7A124B',
  // darker: '#7A0C2E',
};
const OTHER = {
  lighter: '#919EAB29',
  light: '#919EAB29',
  main: '#637381',
  main_16: alpha('#637381', 0.16),
  dark: '#637381',
  darker: '#637381',
};

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8),
};

const WHITE = {
  0: '#FFFFFF',
  500_4: alpha('#FFFFFF', 0.04),
  500_8: alpha('#FFFFFF', 0.08),
  500_12: alpha('#FFFFFF', 0.12),
  500_16: alpha('#FFFFFF', 0.16),
  500_24: alpha('#FFFFFF', 0.24),
  500_32: alpha('#FFFFFF', 0.32),
  500_48: alpha('#FFFFFF', 0.48),
  500_56: alpha('#FFFFFF', 0.56),
  500_80: alpha('#FFFFFF', 0.8),
};

const PRIMARY_MAIN = {
  500_8: alpha('#6F4290', 0.08),
  500_12: alpha('#6F4290', 0.12),
  500_16: alpha('#6F4290', 0.16),
  500_24: alpha('#6F4290', 0.24),
  500_32: alpha('#6F4290', 0.32),
  500_48: alpha('#6F4290', 0.48),
  500_56: alpha('#6F4290', 0.56),
  500_80: alpha('#6F4290', 0.8),
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
  other: createGradient(OTHER.light, OTHER.main),
};

const CHART_COLORS = {
  1: { 1: '#F7D2FF', 2: '#D0AEFF', 3: '#9E86FF', 4: '#826AF9' },
  2: { 1: '#CCFAFF', 2: '#A5F3FF', 3: '#83CFFF', 4: '#2D99FF' },
  3: { 1: '#C9FBCE', 2: '#94F7A8', 3: '#5DE886', 4: '#00CD66' },
  4: { 1: '#FFF3D6', 2: '#FFF7AE', 3: '#FFEF5A', 4: '#FFE700' },
  5: { 1: '#F1C561', 2: '#FFBD98', 3: '#FF8F6D', 4: '#FF6C40' },
  6: { 1: '#E57989', 2: '', 3: '', 4: '' },
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: GREY[800] },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: '#fff' },
  other: { ...OTHER, contrastText: GREY[800] },
  grey: GREY,
  white_color: WHITE,
  primary_main: PRIMARY_MAIN,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const palette = {
  light: {
    ...COMMON,
    mode: 'light',
    text: { primary: GREY[800], secondary: GREY[600], disabled: GREY[500] },
    background: { paper: '#fff', default: '#fff', neutral: GREY[200] },
    action: { active: GREY[600], ...COMMON.action },
  },
  dark: {
    ...COMMON,
    mode: 'dark',
    text: { primary: '#fff', secondary: GREY[500], disabled: GREY[600] },
    background: { paper: GREY[800], default: GREY[900], neutral: GREY[500_16] },
    action: { active: GREY[500], ...COMMON.action },
  },
} as const;

export default palette;
